import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Truck, Search, Phone, Mail, MapPin, FolderTree, Tag, ArrowUpDown, Upload, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [viewingSupplier, setViewingSupplier] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const { canManageSuppliers } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    specialty: '',
    category_ids: []
  });

  useEffect(() => {
    fetchSuppliers();
    fetchCategories();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      specialty: '',
      category_ids: []
    });
    setEditingSupplier(null);
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
        specialty: supplier.specialty || '',
        category_ids: supplier.category_ids || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success('Supplier created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/suppliers/${supplierToDelete.id}`);
      toast.success('Supplier deleted successfully');
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  // Filter suppliers by search term and category
  const searchFilteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.specialty || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || 
      (supplier.category_ids || []).includes(filterCategory);
    
    return matchesSearch && matchesCategory;
  });

  // Apply sorting using the custom hook
  const { sortedData: filteredSuppliers, sortBy, setSortBy, sortOptions } = useSortableData(searchFilteredSuppliers);

  const downloadTemplate = () => {
    window.open(`${API}/suppliers/import/template`, '_blank');
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API}/suppliers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { success_count, error_count, errors } = response.data;
      
      if (success_count > 0) {
        toast.success(`Successfully imported ${success_count} suppliers`);
        fetchSuppliers();
      }
      
      if (error_count > 0) {
        toast.error(`${error_count} rows had errors`);
        errors.slice(0, 3).forEach(err => {
          toast.error(`Row ${err.row}: ${err.error}`);
        });
      }
      
      setImportDialogOpen(false);
      setImportFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Suppliers</h1>
          <p className="text-slate-500 text-sm md:text-base">Manage your supplier information</p>
        </div>
        {canManageSuppliers() && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const params = new URLSearchParams();
                if (searchTerm) params.append('search', searchTerm);
                if (filterCategory !== 'all') params.append('category_id', filterCategory);
                try {
                  await exportWithAuth(`/suppliers/export?${params.toString()}`, 'suppliers_export.csv');
                  toast.success('Suppliers exported successfully');
                } catch (error) {
                  toast.error('Failed to export suppliers');
                }
              }}
              className="rounded-sm"
              data-testid="export-suppliers-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              className="rounded-sm"
              data-testid="import-suppliers-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto"
              data-testid="add-supplier-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Supplier
            </Button>
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search suppliers, specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm h-9"
                data-testid="suppliers-search-input"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-9 px-3 rounded-sm border border-slate-200 bg-white text-sm"
              data-testid="category-filter"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 rounded-sm h-9" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredSuppliers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Contact</TableHead>
                    <TableHead className="hidden md:table-cell">Categories</TableHead>
                    <TableHead className="hidden lg:table-cell">Specialty</TableHead>
                    <TableHead className="hidden sm:table-cell">Phone</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                  {canManageSuppliers() && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.map(supplier => (
                  <TableRow
                    key={supplier.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      setViewingSupplier(supplier);
                      setViewDialogOpen(true);
                    }}
                    data-testid={`supplier-row-${supplier.id}`}
                  >
                    <TableCell>
                      <div className="font-semibold text-slate-900">{supplier.name}</div>
                      <div className="text-xs text-slate-500 sm:hidden">{supplier.contact_name || ''}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 hidden sm:table-cell">{supplier.contact_name || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(supplier.category_ids || []).slice(0, 2).map(catId => (
                          <Badge key={catId} variant="secondary" className="text-xs rounded-sm">
                            {getCategoryName(catId)}
                          </Badge>
                        ))}
                        {(supplier.category_ids || []).length > 2 && (
                          <Badge variant="outline" className="text-xs rounded-sm">
                            +{supplier.category_ids.length - 2}
                          </Badge>
                        )}
                        {(!supplier.category_ids || supplier.category_ids.length === 0) && (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm hidden lg:table-cell max-w-[200px] truncate">
                      {supplier.specialty || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600 font-mono hidden sm:table-cell">{supplier.phone || '-'}</TableCell>
                    <TableCell className="text-slate-600 hidden md:table-cell">{supplier.email || '-'}</TableCell>
                    {canManageSuppliers() && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(supplier)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSupplierToDelete(supplier);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Truck className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No suppliers yet</p>
              <p className="text-sm">Add your first supplier</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh]" data-testid="view-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">{viewingSupplier?.name}</DialogTitle>
            <DialogDescription>Supplier details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto">
            {viewingSupplier?.contact_name && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                  <span className="text-slate-600 text-sm font-medium">
                    {viewingSupplier.contact_name.charAt(0)}
                  </span>
                </div>
                <span className="text-slate-900">{viewingSupplier.contact_name}</span>
              </div>
            )}
            {viewingSupplier?.phone && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Phone className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-slate-900 font-mono">{viewingSupplier.phone}</span>
              </div>
            )}
            {viewingSupplier?.email && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Mail className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-slate-900">{viewingSupplier.email}</span>
              </div>
            )}
            {viewingSupplier?.address && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-slate-900">{viewingSupplier.address}</span>
              </div>
            )}
            {(viewingSupplier?.category_ids || []).length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <FolderTree className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Categories</p>
                  <div className="flex flex-wrap gap-1">
                    {viewingSupplier.category_ids.map(catId => (
                      <Badge key={catId} variant="secondary" className="text-xs rounded-sm">
                        {getCategoryName(catId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {viewingSupplier?.specialty && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Specialty / Materials</p>
                  <span className="text-slate-900 text-sm">{viewingSupplier.specialty}</span>
                </div>
              </div>
            )}
            {viewingSupplier?.notes && (
              <div className="bg-slate-50 rounded-sm p-3">
                <p className="text-xs text-slate-500 mb-1">Notes</p>
                <p className="text-slate-900 text-sm">{viewingSupplier.notes}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
            </DialogTitle>
            <DialogDescription>
              {editingSupplier ? 'Update supplier details' : 'Enter supplier information'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs sm:text-sm">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm h-9"
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact_name" className="text-xs sm:text-sm">Contact Person</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="rounded-sm h-9"
                  data-testid="supplier-contact-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs sm:text-sm">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-sm font-mono h-9"
                  data-testid="supplier-phone-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs sm:text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-sm h-9"
                  data-testid="supplier-email-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address" className="text-xs sm:text-sm">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-sm h-9"
                data-testid="supplier-address-input"
              />
            </div>
            
            {/* Categories Multi-select */}
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Categories Supplied</Label>
              <div className="border border-slate-200 rounded-sm p-2 max-h-32 overflow-y-auto bg-slate-50">
                {categories.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(category => (
                      <label
                        key={category.id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded"
                      >
                        <Checkbox
                          checked={formData.category_ids.includes(category.id)}
                          onCheckedChange={() => handleCategoryToggle(category.id)}
                          className="rounded-sm"
                        />
                        <span className="truncate">{category.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-2">No categories available</p>
                )}
              </div>
              {formData.category_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.category_ids.map(catId => (
                    <Badge key={catId} variant="secondary" className="text-xs rounded-sm">
                      {getCategoryName(catId)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Specialty/Materials Free-text */}
            <div className="space-y-1.5">
              <Label htmlFor="specialty" className="text-xs sm:text-sm">Specialty / Materials Description</Label>
              <Textarea
                id="specialty"
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                className="rounded-sm"
                rows={2}
                placeholder="e.g., Commercial AC units, bulk pricing available, 24hr delivery..."
                data-testid="supplier-specialty-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes" className="text-xs sm:text-sm">Internal Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm"
                rows={2}
                placeholder="Any additional notes..."
                data-testid="supplier-notes-input"
              />
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="supplier-submit-btn">
                {editingSupplier ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Supplier</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{supplierToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm w-full sm:w-auto" data-testid="confirm-delete-supplier-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="import-suppliers-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Import Suppliers</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple suppliers at once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
              <h4 className="font-medium text-sm mb-2">CSV Format</h4>
              <p className="text-xs text-slate-500 mb-3">
                Required: <span className="font-mono bg-slate-200 px-1">name</span><br />
                Optional: contact_name, phone, email, address, specialty, notes
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="rounded-sm"
                data-testid="download-supplier-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="importFile">Select CSV File</Label>
              <Input
                id="importFile"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="rounded-sm"
                data-testid="supplier-import-file-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
              }}
              className="rounded-sm w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto"
              data-testid="confirm-import-suppliers-btn"
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
